import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { setGlobalLoadingContext } from '../utils/globalLoading';

const GlobalLoadingContext = createContext();

export const useGlobalLoading = () => {
  const context = useContext(GlobalLoadingContext);
  if (!context) {
    throw new Error('useGlobalLoading must be used within a GlobalLoadingProvider');
  }
  return context;
};

export const GlobalLoadingProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [loadingCount, setLoadingCount] = useState(0);

  // Show loading when count > 0
  useEffect(() => {
    setIsLoading(loadingCount > 0);
  }, [loadingCount]);

  // Apply wait cursor to body when loading
  useEffect(() => {
    if (isLoading) {
      document.body.style.cursor = 'wait';
      document.body.style.pointerEvents = 'none';
    } else {
      document.body.style.cursor = 'default';
      document.body.style.pointerEvents = 'auto';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.cursor = 'default';
      document.body.style.pointerEvents = 'auto';
    };
  }, [isLoading]);

  const startLoading = useCallback((message = 'Loading...') => {
    setLoadingMessage(message);
    setLoadingCount(prev => prev + 1);
  }, []);

  const stopLoading = useCallback(() => {
    setLoadingCount(prev => Math.max(0, prev - 1));
  }, []);

  const setLoading = useCallback((loading, message = 'Loading...') => {
    if (loading) {
      setLoadingMessage(message);
      setLoadingCount(prev => prev + 1);
    } else {
      setLoadingCount(prev => Math.max(0, prev - 1));
    }
  }, []);

  // Register this context with the utility functions after they're defined
  useEffect(() => {
    setGlobalLoadingContext({ startLoading, stopLoading, setLoading });
  }, [startLoading, stopLoading, setLoading]);

  const value = {
    isLoading,
    loadingMessage,
    startLoading,
    stopLoading,
    setLoading
  };

  return (
    <GlobalLoadingContext.Provider value={value}>
      {children}
      {/* Global loading overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-700 font-medium">{loadingMessage}</p>
          </div>
        </div>
      )}
    </GlobalLoadingContext.Provider>
  );
};
